// Public-source validation logic (pure functions; the Worker does the fetching).

export type CheckResult = "pass" | "fail" | "review" | "error";

export interface CheckOutcome {
  result: CheckResult;
  summary: string;
  details: Record<string, unknown>;
}

/** NPI check-digit validation (Luhn over "80840" + first 9 digits). */
export function isValidNpi(npi: string): boolean {
  if (!/^\d{10}$/.test(npi)) return false;
  const digits = `80840${npi.slice(0, 9)}`;
  let sum = 0;
  for (let i = digits.length - 1, dbl = true; i >= 0; i--, dbl = !dbl) {
    let d = Number(digits[i]);
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  return (10 - (sum % 10)) % 10 === Number(npi[9]);
}

const BEHAVIOR_ANALYST_TAXONOMY = "103K00000X";

interface NppesResponse {
  result_count?: number;
  results?: Array<{
    number: string;
    enumeration_type: string;
    basic: { first_name?: string; last_name?: string; status?: string };
    taxonomies?: Array<{ code: string; desc: string; primary: boolean; state: string | null; license: string | null }>;
  }>;
  Errors?: Array<{ description: string }>;
}

const norm = (s?: string | null) => (s ?? "").trim().toLowerCase().replace(/[^a-z]/g, "");

/** Compares an NPPES registry response with what we have on file. */
export function evaluateNppes(
  provider: {
    npi: string; first_name: string; last_name: string; practice_states: string[];
    licenses?: Array<{ state: string; number: string }>; // licenses on file, compared with NPPES
  },
  response: NppesResponse,
): CheckOutcome {
  if (response.Errors?.length) {
    return { result: "error", summary: `NPPES error: ${response.Errors[0]?.description}`, details: { response } };
  }
  const r = response.results?.[0];
  if (!r) return { result: "fail", summary: `NPI ${provider.npi} not found in NPPES.`, details: {} };

  const problems: string[] = [];
  if (r.enumeration_type !== "NPI-1") problems.push("NPI is not an individual (Type 1) NPI");
  if (r.basic.status && r.basic.status !== "A") problems.push(`NPI status is ${r.basic.status}`);
  if (norm(r.basic.last_name) !== norm(provider.last_name)) {
    problems.push(`last name in NPPES is "${r.basic.last_name}"`);
  }
  if (norm(r.basic.first_name) !== norm(provider.first_name)) {
    problems.push(`first name in NPPES is "${r.basic.first_name}"`);
  }
  const tax = r.taxonomies ?? [];
  const ba = tax.find((t) => t.code === BEHAVIOR_ANALYST_TAXONOMY);
  if (!ba) problems.push("no Behavior Analyst taxonomy (103K00000X)");
  const licensed = new Set(tax.filter((t) => t.license).map((t) => t.state));
  const missingStates = provider.practice_states.filter((s) => !licensed.has(s));
  if (missingStates.length) problems.push(`no license listed in NPPES for ${missingStates.join(", ")}`);
  const digits = (x: string) => x.replace(/\D/g, "").replace(/^0+/, "");
  for (const lic of provider.licenses ?? []) {
    const listed = tax.filter((t) => t.state === lic.state && t.license).map((t) => t.license as string);
    if (listed.length && !listed.some((l) => digits(l) === digits(lic.number))) {
      problems.push(`NPPES lists ${lic.state} license ${listed.join("/")} but our records say ${lic.number}`);
    }
  }

  const details = {
    npi: r.number,
    name: `${r.basic.first_name ?? ""} ${r.basic.last_name ?? ""}`.trim(),
    taxonomies: tax.map((t) => ({ code: t.code, desc: t.desc, state: t.state, license: t.license, primary: t.primary })),
  };
  if (problems.length === 0) {
    return { result: "pass", summary: "NPPES record matches (name, Type 1, Behavior Analyst taxonomy, licenses).", details };
  }
  // A missing license entry in NPPES is common and fixable by the provider; a name/type mismatch is serious.
  const serious = problems.some((p) => p.includes("name") || p.includes("Type 1") || p.includes("status"));
  return { result: serious ? "fail" : "review", summary: `NPPES: ${problems.join("; ")}.`, details };
}

interface NyMedicaidRow {
  npi: string;
  mmis_name: string;
  medicaid_type: string;
  profession_or_service: string;
  enrollment_begin_date?: string;
  updated?: string;
}

/** Evaluates the NY Medicaid Enrolled Provider Listing (health.data.ny.gov) for one NPI. */
export function evaluateNyMedicaid(npi: string, rows: NyMedicaidRow[]): CheckOutcome {
  const lba = rows.filter((r) => /BEHAVIOR ANALYST/i.test(r.profession_or_service));
  const details = {
    npi,
    enrollments: rows.map((r) => ({
      type: r.medicaid_type,
      profession: r.profession_or_service,
      since: r.enrollment_begin_date?.slice(0, 10),
      listing_updated: r.updated?.slice(0, 10),
    })),
  };
  if (lba.length === 0) {
    return {
      result: "review",
      summary: rows.length
        ? "Enrolled in NY Medicaid, but not as a Licensed Behavior Analyst."
        : "Not found in the NY Medicaid Enrolled Provider Listing (new ABA enrollments are under a moratorium since July 2026).",
      details,
    };
  }
  const types = [...new Set(lba.map((r) => r.medicaid_type))].join(", ");
  return { result: "pass", summary: `Enrolled in NY Medicaid as Licensed Behavior Analyst (${types}).`, details };
}

/** Sources that cannot be queried automatically: staff check by hand and record the result. */
export const MANUAL_CHECKS = [
  {
    source: "bacb_registry",
    label: "BACB Certificant Registry",
    url: "https://www.bacb.com/find-a-certificant/",
    why: "BACB's terms prohibit commercial or automated use of the Registry, so it is checked by hand.",
  },
] as const;

/** Lookup pages for the automated state checks (for a human to double-check). */
export const STATE_LOOKUP_LINKS = {
  nysed_license: (licenseNumber?: string | null) =>
    licenseNumber
      ? `https://eservices.nysed.gov/professions/verification-search?licenseNumber=${encodeURIComponent(licenseNumber)}&professionCode=071`
      : "https://eservices.nysed.gov/professions/verification-search",
  nj_dca_license: () => "https://newjersey.mylicense.com/verification/Search.aspx?facility=N",
};

// ------------------------------------------------------------------ NYSED (NY license) lookups
/** Shape of the NYSED Office of the Professions verification record (api.nysed.gov/rosa). */
export interface NysedRecord {
  name?: { value?: string };
  licenseNumber?: { value?: string };
  profession?: { value?: string };
  status?: { value?: string };
  dateOfLicensure?: { value?: string };
  registeredThroughDate?: { value?: string };
  enforcementActions?: unknown[];
}

const toIsoDate = (s?: string) => {
  if (!s) return null;
  const d = new Date(`${s} 12:00 UTC`);
  return Number.isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
};

/** Judges one NYSED license record for a BCBA (NYSED lists names as "LAST FIRST"). */
export function evaluateNysed(
  provider: { first_name: string; last_name: string },
  rec: NysedRecord,
  today = new Date().toISOString().slice(0, 10),
): CheckOutcome & { license?: { number: string; issued_on: string | null; expires_on: string | null } } {
  const name = rec.name?.value ?? "";
  const number = rec.licenseNumber?.value ?? "";
  const status = rec.status?.value ?? "unknown";
  const through = toIsoDate(rec.registeredThroughDate?.value);
  const details = {
    name, licenseNumber: number, profession: rec.profession?.value, status,
    dateOfLicensure: rec.dateOfLicensure?.value, registeredThrough: rec.registeredThroughDate?.value,
    enforcementActions: rec.enforcementActions ?? [],
  };
  const parts = name.toLowerCase().split(/\s+/);
  const nameOk = parts.includes(norm(provider.last_name)) && parts.includes(norm(provider.first_name));
  if (!nameOk) {
    return { result: "review", summary: `NYSED license ${number} is listed for "${name}" - confirm it belongs to this BCBA.`, details };
  }
  if ((rec.enforcementActions ?? []).length > 0) {
    return { result: "fail", summary: `NYSED shows enforcement action(s) on license ${number} - review before billing.`, details };
  }
  if (status !== "Registered" || (through && through < today)) {
    return { result: "fail", summary: `NYSED license ${number} status is ${status}${through ? `, registered through ${rec.registeredThroughDate?.value}` : ""}.`, details };
  }
  return {
    result: "pass",
    summary: `NYSED LBA ${number}: Registered through ${rec.registeredThroughDate?.value}; no enforcement actions.`,
    details,
    license: { number, issued_on: toIsoDate(rec.dateOfLicensure?.value), expires_on: through },
  };
}
